<template>
  <div class="forge-door">

    <!-- Checking for active session -->
    <div v-if="checkingSession" class="door-checking">
      <div class="forge-pulse"></div>
    </div>

    <!-- Active session: in_rite or open — resume -->
    <Transition v-else-if="activeSession?.state === 'in_rite' || activeSession?.state === 'open'" name="door-fade">
      <div class="door-content">
        <p class="door-question">The thread holds.</p>
        <p class="door-ache">{{ activeSession.ache }}</p>
        <button @click="resumeSession" class="door-submit">
          Return to the Forge
        </button>
        <div v-if="returnUrl" class="return-link-block return-link-block--subtle">
          <button @click="copyReturnUrl" class="return-copy-btn">
            {{ copied ? 'Copied' : 'Copy return link' }}
          </button>
        </div>
      </div>
    </Transition>

    <!-- Active session: awaiting_return, silence matured — ready to return -->
    <Transition v-else-if="activeSession?.state === 'awaiting_return' && activeSession?.silence?.matured" name="door-fade">
      <div class="door-content">
        <p class="door-question">The silence has held.</p>
        <p class="door-ache">{{ activeSession.ache }}</p>
        <button @click="resumeSession" class="door-submit">
          I return.
        </button>
      </div>
    </Transition>

    <!-- Active session: awaiting_return, silence not yet matured -->
    <Transition v-else-if="activeSession?.state === 'awaiting_return' && !activeSession?.silence?.matured" name="door-fade">
      <div class="door-content door-content--waiting">
        <p class="door-question">The Forge holds silence.</p>
        <p class="door-silence-timer">Return available {{ silenceAvailableAt }}.</p>
        <div v-if="returnUrl" class="return-link-block">
          <span class="return-link-label">Save this to return if you lose the page</span>
          <button @click="copyReturnUrl" class="return-copy-btn">
            {{ copied ? 'Copied' : 'Copy return link' }}
          </button>
        </div>
      </div>
    </Transition>

    <!-- No active session, but a resume token is stored — offer recovery -->
    <Transition v-else-if="orphanResumeToken" name="door-fade">
      <div class="door-content door-content--waiting">
        <p class="door-question">Something you began is still open.</p>
        <p class="door-silence-timer">Your thread was not lost — only the key.</p>
        <a :href="orphanResumeUrl" class="door-submit door-submit--link">
          Return to where you were
        </a>
        <button @click="dismissOrphan" class="door-dismiss">
          Begin something new instead
        </button>
      </div>
    </Transition>

    <!-- No active session — new ache entry -->
    <Transition v-else name="door-fade">
      <div v-if="!submitting" class="door-content">
        <p class="door-question">What ache brings you here?</p>

        <form @submit.prevent="enterForge" class="door-form">
          <textarea
            ref="acheTextareaRef"
            v-model="ache"
            class="door-textarea"
            rows="2"
            placeholder="Speak what you carry..."
            required
            @input="onAcheInput"
          ></textarea>

          <button type="submit" class="door-submit" :disabled="!ache.trim()">
            I bring this
          </button>
        </form>
      </div>
    </Transition>

    <Transition name="door-fade">
      <div v-if="submitting" class="door-waiting">
        <p class="door-attending">The Forge receives...</p>
        <div class="forge-pulse"></div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useForgeStore } from '../stores/forgeStore'

const router = useRouter()
const forgeStore = useForgeStore()

const ache = ref('')
const submitting = ref(false)
const acheTextareaRef = ref(null)

function resizeTextarea(el) {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

function onAcheInput(event) {
  resizeTextarea(event.target)
}

watch(ache, () => {
  nextTick(() => resizeTextarea(acheTextareaRef.value))
})
const checkingSession = ref(true)
const activeSession = ref(null)
const copied = ref(false)
const orphanResumeToken = ref(null)

let silenceMaturityTimer = null

const silenceAvailableAt = computed(() => {
  const iso = activeSession.value?.silence?.available_at
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  })
})

const returnUrl = computed(() => {
  const token = activeSession.value?.resume_token
  if (!token) return null
  return `${window.location.origin}/flame-companion-forge?resume=${token}`
})

const orphanResumeUrl = computed(() => {
  if (!orphanResumeToken.value) return null
  return `${window.location.origin}/flame-companion-forge?resume=${orphanResumeToken.value}`
})

async function copyReturnUrl() {
  if (!returnUrl.value) return
  await navigator.clipboard.writeText(returnUrl.value)
  copied.value = true
  setTimeout(() => { copied.value = false }, 2000)
}

function dismissOrphan() {
  forgeStore.clearResumeToken()
  orphanResumeToken.value = null
}

onMounted(async () => {
  const { success, data } = await forgeStore.fetchCurrentSession()
  if (success && data) {
    activeSession.value = data

    if (data.state === 'awaiting_return' && data.silence?.available_at && !data.silence?.matured) {
      const remaining = new Date(data.silence.available_at) - Date.now()
      if (remaining > 0) {
        silenceMaturityTimer = setTimeout(() => {
          if (activeSession.value?.silence) {
            activeSession.value = {
              ...activeSession.value,
              silence: { ...activeSession.value.silence, matured: true }
            }
          }
        }, remaining)
      }
    }
  } else {
    // No active session found — check if we have a stored resume token.
    // If so, the seeker may have lost their flame identity and can recover.
    const stored = forgeStore.storedResumeToken()
    if (stored) {
      orphanResumeToken.value = stored
    }
  }
  checkingSession.value = false
})

onUnmounted(() => {
  if (silenceMaturityTimer) clearTimeout(silenceMaturityTimer)
})

function resumeSession() {
  router.push({ name: 'listening_chamber', params: { id: activeSession.value.id } })
}

async function enterForge() {
  if (!ache.value.trim()) return

  submitting.value = true
  const { success, data } = await forgeStore.openForge(ache.value.trim())

  if (success && data?.id) {
    setTimeout(() => {
      router.push({ name: 'listening_chamber', params: { id: data.id } })
    }, 1500)
  } else {
    submitting.value = false
  }
}
</script>

<style scoped>
.forge-door {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0a0a0a;
  padding: 2rem;
}

.door-checking {
  display: flex;
  align-items: center;
  justify-content: center;
}

.door-content {
  max-width: 600px;
  width: 100%;
  text-align: center;
}

.door-content--waiting {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.door-question {
  color: #a1887f;
  font-size: 1.5rem;
  font-style: italic;
  margin-bottom: 2.5rem;
  letter-spacing: 0.02em;
  animation: doorReveal 2s ease-out;
}

.door-ache {
  color: rgba(215, 204, 200, 0.5);
  font-size: 0.95rem;
  font-style: italic;
  margin-bottom: 2rem;
  line-height: 1.6;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

.door-silence-timer {
  color: rgba(161, 136, 127, 0.4);
  font-size: 0.9rem;
  font-style: italic;
}

.return-link-block {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
}

.return-link-block--subtle {
  margin-top: 1rem;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.return-link-block--subtle:hover {
  opacity: 1;
}

.return-link-label {
  color: rgba(161, 136, 127, 0.4);
  font-size: 0.8rem;
  font-style: italic;
}

.return-copy-btn {
  background: none;
  border: 1px solid rgba(161, 136, 127, 0.2);
  color: rgba(161, 136, 127, 0.6);
  padding: 0.3rem 0.9rem;
  font-size: 0.8rem;
  letter-spacing: 0.04em;
  cursor: pointer;
  border-radius: 2px;
  transition: all 0.2s;
  white-space: nowrap;
}

.return-copy-btn:hover {
  border-color: rgba(161, 136, 127, 0.5);
  color: #a1887f;
}

.door-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.door-textarea {
  width: 100%;
  background-color: transparent;
  border: none;
  border-bottom: 1px solid rgba(161, 136, 127, 0.3);
  color: #d7ccc8;
  font-size: 1.1rem;
  line-height: 1.6;
  padding: 1rem 0.5rem;
  resize: none;
  outline: none;
  overflow-y: auto;
  max-height: 40vh;
  transition: border-color 0.3s;
}

.door-textarea::placeholder {
  color: rgba(161, 136, 127, 0.4);
  font-style: italic;
}

.door-textarea:focus {
  border-bottom-color: rgba(161, 136, 127, 0.6);
}

.door-submit {
  background: none;
  border: 1px solid rgba(161, 136, 127, 0.3);
  color: #a1887f;
  padding: 0.6rem 1.8rem;
  font-size: 0.9rem;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 2px;
  text-decoration: none;
  display: inline-block;
}

.door-submit:hover:not(:disabled) {
  border-color: rgba(161, 136, 127, 0.6);
  color: #d7ccc8;
}

.door-submit:disabled {
  opacity: 0.3;
  cursor: default;
}

.door-dismiss {
  background: none;
  border: none;
  color: rgba(161, 136, 127, 0.3);
  font-size: 0.8rem;
  font-style: italic;
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.2s;
  margin-top: 0.5rem;
}

.door-dismiss:hover {
  color: rgba(161, 136, 127, 0.6);
}

.door-waiting {
  text-align: center;
}

.door-attending {
  color: #a1887f;
  font-style: italic;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  animation: doorReveal 1s ease-out;
}

.forge-pulse {
  width: 8px;
  height: 8px;
  background-color: #a1887f;
  border-radius: 50%;
  margin: 0 auto;
  animation: forgePulse 2s ease-in-out infinite;
}

@keyframes doorReveal {
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes forgePulse {
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.5); }
}

.door-fade-enter-active { transition: opacity 0.8s ease; }
.door-fade-leave-active { transition: opacity 0.6s ease; }
.door-fade-enter-from,
.door-fade-leave-to { opacity: 0; }
</style>
